import React, { useEffect, useState, useCallback } from "react";
import GameShell from "../components/GameShell";
import ClaimFlow from "../components/ClaimFlow";
import ScratchCard from "../components/ScratchCard";
import PlayLimitNotice from "../components/PlayLimitNotice";
import { api } from "../lib/api";
import { getDeviceId } from "../lib/device";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function ScratchPage() {
  const [config, setConfig] = useState(null);
  const [canPlay, setCanPlay] = useState(true);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState(null);   // index de la carte choisie
  const [prize, setPrize] = useState(null);      // résultat serveur (label, is_win, spin_id)
  const [result, setResult] = useState(null);    // déclenche ClaimFlow après grattage
  const deviceId = getDeviceId();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/config/grattage");
      setConfig(data);
      const cp = await api.get(`/can-play/${encodeURIComponent(deviceId)}?game=grattage`);
      setCanPlay(cp.data.can_play);
    } catch (e) {
      toast.error("Impossible de charger le jeu");
    }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const pick = async (i) => {
    if (chosen !== null || loading) return;
    if (!canPlay) { toast("🌙 Un seul jeu par jour — reviens demain !"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/scratch", { device_id: deviceId });
      setPrize(data);
      setChosen(i);
      setCanPlay(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      if (e?.response?.status === 429) setCanPlay(false);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setChosen(null); setPrize(null); setResult(null); };

  return (
    <GameShell title={config?.title || "Carte à Gratter"} subtitle={config?.subtitle || "Gratte pour révéler ton lot ✨"}>
      {!config ? (
        <Loader2 className="animate-spin text-white mt-6" size={44} />
      ) : chosen === null ? (
        <>
          <p className="text-white font-display font-bold text-lg mb-4 mt-2">{canPlay ? "Choisis ta carte porte-bonheur 🍀" : "Tu as déjà joué aujourd'hui"}</p>
          <div className="flex gap-3 sm:gap-4" data-testid="scratch-pick">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                data-testid={`scratch-card-${i}`}
                onClick={() => pick(i)}
                disabled={!canPlay || loading}
                className="relative w-[92px] h-[128px] rounded-2xl grid place-items-center transition-transform active:scale-95 disabled:opacity-50"
                style={{ background: "linear-gradient(150deg,#ff5a45,#d8240f)", border: "3px solid #ffde59", boxShadow: "0 8px 0 rgba(0,0,0,.25)" }}
              >
                <span className="font-display font-bold text-5xl" style={{ color: "#ffde59" }}>?</span>
                <span className="absolute bottom-2 text-[10px] font-bold text-white/85">Carte {i + 1}</span>
              </button>
            ))}
          </div>
          {loading && <Loader2 className="animate-spin text-white mt-5" size={28} />}
          {!canPlay && !loading && <PlayLimitNotice />}
        </>
      ) : (
        <>
          {/* Cartes : la choisie devient grattable, les autres se retournent grisées */}
          <div className="flex gap-3 items-center mb-1" data-testid="scratch-chosen">
            {[0, 1, 2].map((i) =>
              i === chosen ? (
                <div key={i} className="rounded-2xl overflow-hidden" style={{ border: "3px solid #ffde59", background: "#fff7ef", boxShadow: "0 8px 0 #a81a0c" }}>
                  <div style={{ background: "#ff2e18", padding: "6px", textAlign: "center" }}>
                    <b style={{ color: "#fff", fontSize: 11, letterSpacing: ".5px" }}>YÜM · TICKET GOURMAND</b>
                  </div>
                  <ScratchCard prize={prize} onReveal={() => setResult(prize)} />
                </div>
              ) : (
                <div key={i} className="w-[70px] h-[98px] rounded-xl grid place-items-center opacity-50"
                  style={{ background: "#3b262e", border: "2px solid #5b4a50" }}>
                  <span className="text-2xl">🎴</span>
                </div>
              )
            )}
          </div>
          <p className="text-white/80 text-sm mt-2">Gratte la carte pour découvrir ton lot ✨</p>
        </>
      )}

      <ClaimFlow result={result} onClose={reset} />
    </GameShell>
  );
}
